import React from 'react'
import tw, { styled } from 'twin.macro'
import { GatsbyImage, getImage, StaticImage } from 'gatsby-plugin-image'
import { useStaticQuery, graphql, Link, navigate } from 'gatsby'
import { AnimatePresence, motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useBreakpoint } from '@hooks/useBreakpoint'
import Chevron from '@elements/Chevron'
import { strings } from '../../static/strings'
import parse from 'html-react-parser'
import Modal from '@elements/Modal'
import { useStateContext } from '@context/stateContext'

const Section = tw.section``
const SectionWrap = tw.div`relative flex flex-col`
const HeadlineWrap = tw.div`px-offset-xs max-w-screen-xl w-full mx-auto relative translate-y-spacingXs-master lg:translate-y-spacingS-master z-10`
const Headline = tw.p`w-full md:w-1/2 2xl:max-w-[750px] xl:w-2/3`
const Wrap = tw(motion.div)`max-w-screen-xl px-offset-xs mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-10 w-full`
const Item = tw(motion.div)`relative`
const ImageWrap = tw.div`h-full w-full`
const Card = tw(motion.div)`relative block`
const Overline = tw.p`font-futuraBook pb-5 tracking-1 text-sm text-mediumDark capitalize`
const Title = tw.p`font-futuraBook text-copyXl md:text-3xl italic tracking-1 md:tracking-3 pb-4`
const Text = tw.p`font-futuraBook text-sm md:text-copyXl italic tracking-1 pb-4`
const Content = tw.div`p-5 text-dark`
const MoreLink = tw(Link)`text-primary leading-[21px] tracking-1 md:tracking-2 uppercase text-[14px] pt-5`
const VideoWrap = tw(motion.div)`w-full h-full absolute inset-0`

const CategoriesWrap = tw.div`px-offset-xs grid grid-flow-col mb-spacingS-xs max-w-screen-xl mx-auto`
const Category = styled.button`
	${tw`px-8 py-3 text-sm uppercase transition-colors border-b xl:py-5 lg:text-xs xl:text-sm lg:px-4 xl:px-8 border-b-light tracking-1 md:tracking-2 text-mediumDark`}

	&.active {
		${tw`border-b-primary`}
	}
`

const ModalList = tw.div`flex flex-col`

const Flex = tw.div`px-5 flex items-center gap-4`
const ShowMore = tw(
	motion.div
)`uppercase  mx-auto relative pt-2 mt-8 text-sm cursor-pointer text-primary tracking-1 md:tracking-2 before:absolute before:w-24 before:bottom-full before:left-0 before:h-px before:bg-light`

const WpAcfTeamoverviewBlock = ({ data, locale }) => {
	const { isTablet } = useBreakpoint()
	const [modalOpened, setModalOpened] = React.useState(false)
	const module = data.modulesTeamOverview
	const lang = locale.substr(0, 2)
	const teamQuery = useStaticQuery(graphql`
		{
			allTeam: allWpTeam(sort: { order: ASC, fields: dateGmt }, filter: { locale: { locale: { eq: "de_DE" } } }) {
				edges {
					node {
						postTypeTeam {
							teaserFields {
								image {
									localFile {
										childImageSharp {
											gatsbyImageData(width: 1000, quality: 85, formats: [AUTO, WEBP])
										}
									}
									altText
								}
								video {
									localFile {
										publicURL
									}
								}
								overline
								text
								title
							}
							teamHeader {
								imagePrimary {
									localFile {
										childImageSharp {
											gatsbyImageData(width: 1000, quality: 85, formats: [AUTO, WEBP])
										}
									}
									altText
								}
							}
						}
						uri
						title
						slug
						blocksJSON
						locale {
							locale
						}
						teamCategory {
							nodes {
								name
								slug
							}
						}
						translated {
							postTypeTeam {
								teaserFields {
									overline
									text
									title
								}
							}
							uri
							title
							slug
							teamCategory {
								nodes {
									name
									slug
								}
							}
							locale {
								locale
							}
						}
					}
				}
			}
			teamCategories: allWpTeamCategory {
				edges {
					node {
						slug
						name
					}
				}
			}
		}
	`)
	const items = teamQuery.allTeam?.edges
	const [activeCategory, setActiveCategory] = React.useState('alle')
	const [showMoreState, setShowMoreState] = React.useState(module.layout === 'small' ? 3 : 9)
	const [filteredItems, setFilteredItems] = React.useState(null)

	const filterCards = (category) => {
		setShowMoreState(module.layout === 'small' ? 3 : 9)
		if (category === 'alle') {
			const __filtered = filterDrivers()
			return setFilteredItems(__filtered)
		}
		return setFilteredItems(items.filter((item) => item.node.teamCategory?.nodes[0]?.slug === category))
	}
	const filterDrivers = () => {
		const driver = items.filter((item) => item.node.teamCategory?.nodes[0]?.slug === 'driver')
		const management = items.filter((item) => item.node.teamCategory?.nodes[0]?.slug === 'management')
		const engineer = items.filter((item) => item.node.teamCategory?.nodes[0]?.slug === 'engineers')
		const administration = items.filter((item) => item.node.teamCategory?.nodes[0]?.slug === 'administration')
		const mechanic = items.filter((item) => item.node.teamCategory?.nodes[0]?.slug === 'mechanics')
		const logistic = items.filter((item) => item.node.teamCategory?.nodes[0]?.slug === 'logistics-specalists')
		return driver.concat(management, engineer, administration, mechanic, logistic)
	}

	const categoriesOrder = ['driver', 'management', 'engineers', 'administration', 'mechanics', 'logistics-specalists']

	React.useEffect(() => {
		if (filteredItems === null) {
			const __filtered = filterDrivers()
			setFilteredItems(__filtered)
		}
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const activeCategoryName = (category) => {
		return teamQuery.teamCategories.edges.filter((item) => item.node.slug === category)
	}
	const { language } = useStateContext()

	const getTranslatedCategoryName = (string) => {
		const result = Object.keys(strings['de'].team).find((key) => strings['de'].team[key].toUpperCase() === string.toUpperCase())
		if (language === 'en') {
			return strings[language].team[result] ? strings[language].team[result] : string
		}
		return string
	}

	return (
		<Section>
			{module.layout === 'small' && (
				<HeadlineWrap>
					<Headline className='headline-h2'>{parse(strings[lang].teamMembers)}</Headline>
				</HeadlineWrap>
			)}
			<SectionWrap className={`${module.layout === 'small' ? 'py-16 lg:py-28 bg-superLight' : 'emf-pb-spacing'}`}>
				<CategoriesWrap className='w-full'>
					<Category
						className={`${activeCategory === 'alle' ? 'active' : ''}`}
						onClick={() => {
							setActiveCategory('alle')
							filterCards('alle')
						}}
					>
						{strings[lang].all}
					</Category>
					{isTablet && (
						<Category onClick={() => setModalOpened(true)} className={`flex items-center justify-center ${activeCategory !== 'alle' ? 'active' : ''}`}>
							{activeCategory !== 'alle' ? (lang === 'de' ? activeCategoryName(activeCategory)[0].node.name : activeCategoryName(activeCategory)[0].node.slug) : 'Filter anzeigen'}{' '}
							<svg className='ml-3' width='14' height='13' viewBox='0 0 14 13' fill='none' xmlns='http://www.w3.org/2000/svg'>
								<path
									d='M13.0778 0C13.5643 0 13.8597 0.12264 13.9639 0.367925C14.0682 0.613209 13.9465 0.921668 13.599 1.29331L9.95028 5.19554C9.61668 5.55232 9.33173 5.99828 9.09543 6.53345C8.85913 7.06861 8.74098 7.52201 8.74098 7.89365V11.1492L5.25902 13V8.36192C5.25902 7.84162 5.14435 7.27673 4.915 6.66724C4.68564 6.05774 4.39722 5.56718 4.04972 5.19554L0.400963 1.29331C0.0534603 0.921668 -0.0681637 0.613209 0.036087 0.367925C0.140338 0.12264 0.43571 0 0.922214 0H13.0778Z'
									fill='#666666'
								/>
							</svg>
						</Category>
					)}
					{!isTablet &&
						categoriesOrder.map((category) => {
							const categoryName = teamQuery.teamCategories.edges.filter((item) => item.node.slug === category)

							return (
								<Category
									key={`${category}`}
									className={`${activeCategory === category ? 'active' : ''}`}
									onClick={() => {
										setActiveCategory(category)
										filterCards(category)
									}}
								>
									{getTranslatedCategoryName(categoryName[0]?.node?.name ?? '')}
								</Category>
							)
						})}
				</CategoriesWrap>
				<Wrap transition={{ staggerChildren: 0.5 }}>
					{filteredItems?.length > 0 &&
						filteredItems !== null &&
						filteredItems.slice(0, showMoreState).map(({ node }, index) => {
							return <TeamCard key={`team-card-${index}`} item={node} index={index} lang={lang} locale={locale} activeCategory={activeCategory} />
						})}
				</Wrap>
				{filteredItems?.length > showMoreState && (
					<ShowMore onClick={() => setShowMoreState((current) => (module.layout === 'small' ? 3 : 9) + current)}>
						<Flex>
							<Chevron state={'down'} /> {strings[lang].showMore}
						</Flex>
					</ShowMore>
				)}
			</SectionWrap>
			<Modal state={modalOpened} onClick={() => setModalOpened(false)}>
				<ModalList>
					{categoriesOrder.map((category) => {
						const categoryName = teamQuery.teamCategories.edges.filter((item) => item.node.slug === category)
						return (
							<Category
								key={`${category}`}
								className={`py-5 ${activeCategory === category ? 'active' : ''}`}
								onClick={() => {
									setActiveCategory(category)
									filterCards(category)
									setModalOpened(false)
								}}
							>
								{getTranslatedCategoryName(categoryName[0]?.node?.name ?? '')}
							</Category>
						)
					})}
				</ModalList>
			</Modal>
		</Section>
	)
}

const TeamCard = ({ item, index, activeCategory, lang, locale }) => {
	const [hovering, setHovering] = React.useState(false)
	const videoRef = React.useRef()
	const itemData = item
	const { isMobile } = useBreakpoint()
	const handleHovering = (state) => {
		if (!itemData.postTypeTeam?.teaserFields?.video?.localFile?.publicURL) return
		if (state) {
			videoRef.current.setAttribute('autoPlay', true)
			videoRef.current.play()
		}
		if (!state) {
			videoRef.current.pause()
			videoRef.current.currentTime = 0
			videoRef.current.setAttribute('autoPlay', false)
		}
		setHovering(state)
	}
	const [ref, isVisible] = useInView({ threshold: 0.5 })

	React.useEffect(() => {
		if (isVisible && isMobile) {
			handleHovering(true)
		}
		if (!isVisible && isMobile) {
			handleHovering(false)
		}
	}, [isVisible, isMobile]) // eslint-disable-line react-hooks/exhaustive-deps

	const getLocaleContent = (type) => {
		if (itemData.locale.locale === locale) {
			if (type === 'url') return itemData?.uri
			if (type === 'text') return itemData?.postTypeTeam?.teaserFields?.text ?? null
			if (type === 'overline') return itemData?.postTypeTeam?.teaserFields?.overline ?? null
		}
		if (itemData.translated[0]?.locale?.locale === locale) {
			if (type === 'url') return itemData?.translated[0]?.uri
			if (type === 'text') return itemData?.translated[0]?.postTypeTeam?.teaserFields?.text ?? null
			if (type === 'overline') return itemData?.translated[0]?.postTypeTeam?.teaserFields?.overline ?? null
		}
		return false
	}

	const cardNavigateCheck = () => {
		if (itemData.blocksJSON !== '[]' && itemData.teamCategory?.nodes[0]?.slug === 'driver' && getLocaleContent('url')) {
			return navigate(getLocaleContent('url'))
		}
	}

	return (
		(itemData.teamCategory?.nodes[0]?.slug === activeCategory || activeCategory === 'alle') && (
			<AnimatePresence key={index} exitBeforeEnter>
				<Item ref={ref} custom={index} variants={cardVariants} initial='initial' animate='animate'>
					<Card onMouseEnter={() => !hovering && !isMobile && handleHovering(true)} onMouseLeave={() => hovering && !isMobile && handleHovering(false)}>
						<ImageWrap>
							<div
								onClick={() => cardNavigateCheck()}
								className={`block h-full aspect-w-1 aspect-h-1 ${itemData.blocksJSON !== '[]' && itemData.teamCategory?.nodes[0]?.slug === 'driver' && 'cursor-pointer'}`}
							>
								{!itemData.postTypeTeam?.teaserFields?.image?.localFile && <StaticImage className='w-full h-full' src='../../images/platzhalter.jpg' alt='' />}
								{itemData.postTypeTeam?.teaserFields?.image?.localFile && (
									<GatsbyImage
										className='w-full h-full !absolute'
										imgClassName='!absolute'
										image={getImage(itemData.postTypeTeam?.teaserFields?.image?.localFile)}
										alt={itemData.postTypeTeam?.teaserFields?.image?.altText}
									/>
								)}
								{itemData.postTypeTeam?.teaserFields?.video?.localFile?.publicURL && (
									<VideoWrap variants={imageVariants} initial='hidden' animate={hovering ? 'shown' : 'hidden'}>
										<video
											ref={videoRef}
											controls={false}
											autoPlay={false}
											muted
											playsInline
											src={itemData.postTypeTeam?.teaserFields?.video?.localFile?.publicURL}
											className='object-cover w-full h-full'
										></video>
									</VideoWrap>
								)}
							</div>
						</ImageWrap>
						<Content>
							<Overline>{parse(getLocaleContent('overline') || (lang === 'de' ? itemData.teamCategory?.nodes[0]?.name : itemData.teamCategory?.nodes[0]?.slug))}</Overline>
							<Title>{itemData.postTypeTeam?.teaserFields?.title ?? itemData.title}</Title>
							{itemData.postTypeTeam?.teaserFields?.text && <Text>{getLocaleContent('text')}</Text>}
							{itemData.blocksJSON !== '[]' && itemData.teamCategory?.nodes[0]?.slug === 'driver' && <MoreLink to={getLocaleContent('url')}>{strings[lang].readMore}</MoreLink>}
						</Content>
					</Card>
				</Item>
			</AnimatePresence>
		)
	)
}
const cardVariants = {
	initial: {
		x: -30,
		opacity: 0,
	},
	animate: (index) => ({
		x: 0,
		opacity: 1,
		transition: {
			type: 'tween',
			duration: 0.3,
			delay: index * 0.015,
		},
	}),
}
const imageVariants = {
	shown: {
		opacity: 1,
		transition: {
			duration: 0.3,
		},
	},
	hidden: {
		opacity: 0,
		transition: {
			duration: 1,
		},
	},
}

export default WpAcfTeamoverviewBlock
